import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { GetInsurancePlan } from "./GetInsurancePlan";

// Tipos para os parâmetros e retorno do hook
interface FetchInsurancePlansParams {
  status?: string;
  count?: number;
  name?: string;
  ownedBy?: string;
}

interface InsurancePlan {
  resourceType: string;
  id: string;
  name?: string; // Nome do plano
  status?: string; // Status do plano (ex: 'active')
  // Adicione outros campos relevantes conforme necessário
}

interface Bundle {
  entry?: Array<{ resource: InsurancePlan }>;
}

interface UseInsurancePlansReturn {
  insurancePlans: InsurancePlan[];
  loading: boolean;
  fetchInsurancePlans: (
    params: FetchInsurancePlansParams
  ) => Promise<InsurancePlan[]>;
  clearInsurancePlans: () => void;
}

// Hook para buscar e gerenciar planos de saúde
export const useInsurancePlans = (): UseInsurancePlansReturn => {
  const [insurancePlans, setInsurancePlans] = useState<InsurancePlan[]>([]);
  const [loading, setLoading] = useState(false);

  // Função para buscar planos de saúde
  const fetchInsurancePlans = useCallback(
    async ({ status, count, name, ownedBy }: FetchInsurancePlansParams) => {
      setLoading(true);
      try {
        // Buscar planos de saúde com os parâmetros fornecidos
        const response = (await GetInsurancePlan({
          status,
          count,
          name,
          ownedBy,
        })) as Bundle;

        if (response?.entry?.length) {
          // Filtrar apenas recursos do tipo InsurancePlan
          const plansList = response.entry
            .filter(
              (entry): entry is { resource: InsurancePlan } =>
                entry.resource?.resourceType === "InsurancePlan"
            )
            .map((entry) => entry.resource);

          setInsurancePlans(plansList);
          return plansList;
        }

        // Se não houver entries, retorna array vazio
        setInsurancePlans([]);
        return [];
      } catch (error) {
        toast.error("Erro ao carregar planos de saúde");
        setInsurancePlans([]);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Função para limpar o estado dos planos
  const clearInsurancePlans = useCallback(() => {
    setInsurancePlans([]);
  }, []);

  return {
    insurancePlans,
    loading,
    fetchInsurancePlans,
    clearInsurancePlans,
  };
};
